/**
 * WelcomeStep
 *
 * First onboarding screen - user segment selection.
 * Editorial design with staggered reveals and refined interactions.
 *
 * Design: Inspired by Collins, Pentagram - quiet luxury, intentional typography,
 * generous white space, sophisticated motion.
 */

import { useState } from 'react';
import { useSetAtom } from 'jotai';
import { motion } from 'framer-motion';
import {
  Building2,
  Camera,
  Users,
  Home,
  Palette,
  HelpCircle,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { cn } from '../../../lib/cn';
import type { UserSegment } from '../../../lib/supabase';
import {
  OnboardingLayout,
  OnboardingStepContainer,
  OnboardingHeading,
} from '../OnboardingLayout';
import { onboardingLoadingAtom } from '../../../state/onboardingAtoms';
import type { OnboardingStepProps } from '../OnboardingRouter';

interface SegmentOption {
  value: UserSegment;
  label: string;
  description: string;
  icon: typeof Building2;
}

const SEGMENT_OPTIONS: SegmentOption[] = [
  {
    value: 'agent',
    label: 'Real Estate Agent',
    description: 'Elevate your listings',
    icon: Building2,
  },
  {
    value: 'photographer',
    label: 'RE Photographer',
    description: 'Expand your services',
    icon: Camera,
  },
  {
    value: 'brokerage',
    label: 'Brokerage / Team',
    description: 'Scale your operation',
    icon: Users,
  },
  {
    value: 'fsbo',
    label: 'Selling My Home',
    description: 'Stand out to buyers',
    icon: Home,
  },
  {
    value: 'homeowner',
    label: 'Design & Decor',
    description: 'Visualize possibilities',
    icon: Palette,
  },
  {
    value: 'other',
    label: 'Just Exploring',
    description: "See what's possible",
    icon: HelpCircle,
  },
];

// Stagger animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function WelcomeStep({ onStepComplete, onboardingData }: OnboardingStepProps) {
  const { user, profile, refreshProfile } = useAuth();
  const [selectedSegment, setSelectedSegment] = useState<UserSegment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setLoading = useSetAtom(onboardingLoadingAtom);

  const firstName = profile?.full_name?.split(' ')[0] || '';

  const handleSegmentSelect = async (segment: UserSegment) => {
    setSelectedSegment(segment);
    setIsSubmitting(true);
    setLoading(true);

    try {
      if (user) {
        const { error } = await supabase
          .from('profiles')
          .update({
            user_segment: segment,
            updated_at: new Date().toISOString(),
          })
          .eq('id', user.id);

        if (error) throw error;
        await refreshProfile();
      }

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'onboarding_segment_selected',
          segment,
          session_id: onboardingData.session_id,
        });
      }

      await onStepComplete('welcome', {});
    } catch (error) {
      console.error('Error saving segment:', error);
      await onStepComplete('welcome');
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout showProgress={true} showSkip={false}>
      <OnboardingStepContainer maxWidth="4xl">
        <OnboardingHeading
          eyebrow="Let's begin"
          title={firstName ? `Welcome, ${firstName}` : 'Welcome to the studio'}
          subtitle="What brings you here today? This helps us personalize your experience."
        />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
        >
          {SEGMENT_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedSegment === option.value;

            return (
              <motion.button
                key={option.value}
                variants={itemVariants}
                onClick={() => handleSegmentSelect(option.value)}
                disabled={isSubmitting}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  'group relative p-6 sm:p-8 rounded-3xl text-left transition-all duration-300',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2',
                  isSelected
                    ? 'bg-charcoal text-alabaster shadow-lift'
                    : 'bg-white border border-stone/30 hover:border-stone/50 hover:shadow-soft'
                )}
              >
                {/* Icon */}
                <div
                  className={cn(
                    'size-12 rounded-2xl flex items-center justify-center mb-5 transition-colors duration-300',
                    isSelected
                      ? 'bg-white/10'
                      : 'bg-stone/20 group-hover:bg-olive/10'
                  )}
                >
                  <Icon
                    className={cn(
                      'size-6 transition-colors duration-300',
                      isSelected
                        ? 'text-alabaster'
                        : 'text-sand group-hover:text-olive'
                    )}
                  />
                </div>

                {/* Content */}
                <h3
                  className={cn(
                    'font-sans font-semibold text-lg mb-1 transition-colors duration-300',
                    isSelected ? 'text-alabaster' : 'text-charcoal'
                  )}
                >
                  {option.label}
                </h3>
                <p
                  className={cn(
                    'text-sm font-sans transition-colors duration-300',
                    isSelected ? 'text-alabaster/70' : 'text-sand'
                  )}
                >
                  {option.description}
                </p>

                {/* Arrow indicator */}
                <div
                  className={cn(
                    'absolute bottom-6 right-6 sm:bottom-8 sm:right-8 size-8 rounded-full flex items-center justify-center',
                    'transition-all duration-300',
                    isSelected
                      ? 'bg-white/20 text-alabaster'
                      : 'bg-transparent text-stone group-hover:bg-stone/20 group-hover:text-charcoal'
                  )}
                >
                  <ArrowRight className="size-4" />
                </div>

                {/* Selection indicator */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
                    className="absolute top-4 right-4 size-6 bg-olive rounded-full flex items-center justify-center"
                  >
                    <svg
                      className="size-3.5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </motion.div>

        {/* Loading overlay */}
        {isSubmitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-alabaster/90 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <div className="text-center">
              <div className="size-12 border-2 border-charcoal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sand font-sans text-sm">Personalizing your experience</p>
            </div>
          </motion.div>
        )}
      </OnboardingStepContainer>
    </OnboardingLayout>
  );
}
